import { AttendeePriority } from '../interfaces/interface';
import { SSMService } from 'src/services/aws/SSMService';
import { logger } from 'src/logger';

// Attendee path card preview images
import previewSmartUpload from '../assets/preview-smart-upload.svg';
import previewCloneMeeting from '../assets/preview-clone-meeting.svg';
import previewAddManually from '../assets/preview-add-manually.svg';

export const MAX_ATTACHMENTS = 5;

export const HUDDLE_DEMO_VIDEO_URL = 'https://broadcast.amazon.com/videos/1897384';

/** Color map for priority badges — shared across all attendee-related components */
export const PRIORITY_COLORS: Record<AttendeePriority, { background: string; color: string }> = {
  [AttendeePriority.Critical]: { background: '#d91515', color: '#fff' },
  [AttendeePriority.Required]: { background: '#0972d3', color: '#fff' },
  [AttendeePriority.Optional]: { background: '#e9ebed', color: 'rgb(95, 107, 122)' }
};

/** Color map for employee badge types — shared across profile picture components */
export const BADGE_COLOR_MAP: Record<string, { background: string; color: string }> = {
  blue: { background: '#0073bb', color: '#fff' },
  orange: { background: '#ec7211', color: '#fff' },
  red: { background: '#d91515', color: '#fff' },
  purple: { background: '#8b5cf6', color: '#fff' },
  silver: { background: '#adb5bd', color: '#333' },
  gold: { background: '#bf9b30', color: '#fff' }
};

/**
 * Prompt for AI-powered time slot summary generation.
 */
export const TIMESLOT_SUMMARY_PROMPT = `You are a meeting scheduling assistant. You receive structured JSON data about a meeting with PRE-RESOLVED vote data and produce a concise scheduling recommendation.

Input
You will receive:

Meeting metadata (title, date, duration, timezone, organizer, on behalf of)
Attendee list with: name, level, role (critical/required/optional), EA name (if applicable), proxyFor (name of the principal they are proxying for, if applicable)
Proposed time slots, each containing an "attendeeVotes" array where every attendee's vote is already resolved:
  - { "name": "Attendee Name", "vote": "up" | "down" | "none", "via": "EA Name" (optional) }
  - "up" = 👍, "down" = 👎, "none" = ⏳ No response
  - "via" indicates who voted on behalf of this attendee (EA, proxy, etc.)
Comments on time slots (if any)

Rules

CRITICAL: The vote data is PRE-RESOLVED. Do NOT re-interpret or cross-reference votes. Use the attendeeVotes array exactly as provided. Each entry tells you the attendee's name, their vote direction, and who voted on their behalf (if anyone).

A critical attendee's 👎 eliminates that slot. Flag it with 🚫.
All critical attendees must have voted (not "none") for a slot to be recommended. If any critical attendee has vote="none", the slot cannot be fully confirmed.
Vote weighting for recommendation: Critical = must-have, Required = strong signal, Optional = nice-to-have.
Comments override votes. If someone voted 👍 but commented "tight, prefer the other", note the conflict.
No vote (⏳) is a risk, not neutral.

Output Format
You MUST produce valid markdown. Follow this structure exactly. Preserve all line breaks between sections. Use markdown horizontal rules (---) to separate major sections. Do NOT use markdown table syntax (pipes). Use bold labels and lists instead.

# Meeting: [title]

- **Date:** [date or "Multiple dates proposed" if more than one]
- **Duration:** [duration]
- **Timezone:** [timezone]
- **Attendees:** [count] ([critical count] critical, [required count] required, [optional count] optional)
- **Organizer:** [name] (on behalf of [name], if applicable)

---

## Slot Summary

For each slot, use the provided upCount, downCount, and noneCount values directly.

- **[date, start–end] [timezone]** — 👍 [upCount] 👎 [downCount] ⏳ [noneCount]

---

## Attendee Breakdown

For each attendee, list their per-slot votes. Use the "via" field for attribution. Always list 👍 slots before 👎 slots.

### 🟥 Critical

- **[Name]** — [Level] — [👍/👎/⏳] [details — if 👎, include "Blocked [slot time]"]

### 🟦 Required

- **[Name]** — [Level] — [👍/👎/⏳] [details — per-slot breakdown if mixed votes]

### ⬜ Optional

- **[Name]** — [Level] — EA: [EA Name or "—"] — [👍/👎/⏳] [details]

---

## Recommendation

[emoji] [recommendation line]

---

## Suggested Actions

- [action]
- [action]
- [action]

IMPORTANT: Do NOT use markdown table syntax (| pipes). Always use bullet lists with bold labels as shown above. Always include markdown horizontal rules (---) between major sections. Always include the timezone in each time slot line. Use bullet points (not numbered lists) for suggested actions. Use "No response" for ⏳ status text. For mixed votes across slots, show per-slot breakdown (e.g., "👍 Slot 1, 👎 Slot 2").

Recommendation logic
If clear winner: ✅ Recommended: [slot]. [One sentence why.]
If slot blocked by critical: 🚫 [slot] is blocked. [Name] (Critical) voted 👎.
If no clear winner: ⚠️ [Explain tradeoff in one sentence.]
If no votes: 📭 No votes received yet.
If tie: 🔄 Tie between [slots]. [Tiebreaker suggestion.]

Rules for suggested actions
Always route outreach through EAs when available
If a critical attendee voted 👎, suggest checking their availability for alternative times via their EA
If only one slot exists, suggest adding alternatives
Keep to 3-4 actions max

Tone
Direct, scannable. No filler. Bullet points. Optimize for a quick glance that tells the organizer exactly what to do next.`;

/**
 * Configuration constants for EA Huddle meeting operations.
 */
export const PASTE_ATTENDEES_PLACEHOLDER = 'Paste names, aliases, or emails - separated by comma, semicolon or one per line...';

export const MEETING_CONSTANTS = {
  ATTENDEE_BATCH_SIZE: 8,
  BATCH_DELAY_MS: 500,
  PAGE_SIZE: 4,
  FAVORITES_PAGE_SIZE: 5,
  MAX_RECENT_MEETINGS: 100,
  MAX_DOCUMENT_ATTENDEES: 100,
  EMPLOYEE_CACHE_TTL_SECONDS: 3600,
  /** Browser-side employee cache TTL (30 minutes). Covers in-memory Map + sessionStorage. */
  BROWSER_CACHE_TTL_MS: 30 * 60 * 1000,
  MAX_SUBJECT_LENGTH: 100,
  MAX_AGENDA_LENGTH: 2000
} as const;

/** Default fallback URLs used when SSM parameters are unavailable */
const DEFAULT_OUTLOOK_CALENDAR_DEEPLINK_URL = 'https://outlook.cloud.microsoft/calendar/deeplink/compose';
const DEFAULT_OUTLOOK_MAIL_DEEPLINK_URL = 'https://outlook.cloud.microsoft/mail/deeplink/compose';

/** SSM parameter paths for Outlook deeplink URLs */
const OUTLOOK_CALENDAR_DEEPLINK_SSM_PARAM = '/das-finsuite/fe/outlook-calendar-deeplink-url';
const OUTLOOK_MAIL_DEEPLINK_SSM_PARAM = '/das-finsuite/fe/outlook-mail-deeplink-url';

let deeplinkPromise: Promise<{ calendarUrl: string; mailUrl: string }> | null = null;

/** Load Outlook deeplink URLs from SSM (cached). Falls back to defaults on error. */
export const getOutlookDeeplinkUrls = (): Promise<{ calendarUrl: string; mailUrl: string }> => {
  if (!deeplinkPromise) {
    deeplinkPromise = Promise.all([
      SSMService.retrieveParameter(OUTLOOK_CALENDAR_DEEPLINK_SSM_PARAM),
      SSMService.retrieveParameter(OUTLOOK_MAIL_DEEPLINK_SSM_PARAM)
    ])
      .then(([calendarUrl, mailUrl]) => {
        logger.info(`Outlook deeplink URLs loaded from SSM: calendar=${calendarUrl}, mail=${mailUrl}`);
        return { calendarUrl, mailUrl };
      })
      .catch((err) => {
        logger.error(`Failed to load Outlook deeplink URLs from SSM, using defaults: ${err}`);
        return { calendarUrl: DEFAULT_OUTLOOK_CALENDAR_DEEPLINK_URL, mailUrl: DEFAULT_OUTLOOK_MAIL_DEEPLINK_URL };
      });
  }
  return deeplinkPromise;
};

// ============================================================================
// Attendee Path Configuration
// ============================================================================

/**
 * The three available methods for adding attendees to a meeting.
 */
export type AttendeePath = 'upload' | 'clone' | 'manual';

/**
 * Describes a button rendered within an attendee path card.
 */
export interface AttendeePathButton {
  label: string;
  icon: string | null;
  variant: 'primary' | 'secondary';
}

/**
 * Describes a single attendee path option card configuration.
 */
export interface AttendeePathOption {
  path: AttendeePath;
  title: string;
  description: string;
  buttons: AttendeePathButton[];
  previewImage: string;
}

/**
 * Configuration for the three attendee path cards displayed on the landing page.
 * Each entry defines the card's content and action buttons.
 */
export const ATTENDEE_PATH_OPTIONS: readonly AttendeePathOption[] = [
  {
    path: 'upload',
    title: 'Smart Upload',
    description: 'Drop a file here or click to upload. Huddle+ extracts attendees automatically',
    previewImage: previewSmartUpload,
    buttons: [{ label: 'Upload', icon: 'ai', variant: 'primary' }]
  },
  {
    path: 'clone',
    title: 'Clone a Meeting',
    description: 'Pick a past meeting or paste a link to import attendees.',
    previewImage: previewCloneMeeting,
    buttons: [{ label: 'Clone Meeting', icon: 'search', variant: 'primary' }]
  },
  {
    path: 'manual',
    title: 'Add Manually',
    description: 'Search and add attendees one at a time.',
    previewImage: previewAddManually,
    buttons: [{ label: 'Start Fresh', icon: null, variant: 'primary' }]
  }
] as const;

// ── Product Contacts ───────────────────────────────────────────────────────────

export const HUDDLE_CONTACTS = [{ alias: 'jaramja' }, { alias: 'mkteja' }, { alias: 'hbhardwj' }, { alias: 'shruagar' }] as const;

export const HUDDLE_SLACK_CHANNEL = {
  name: '#huddle-plus',
  href: 'https://amazon.enterprise.slack.com/archives/C0A38R2A42J'
} as const;
